import React, { useEffect, useRef, useState } from "react";
import { v4 as randomUUID } from "uuid";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PatientBillInfoDto } from "../../../../api-client";
import { useLocationId, useUserRole } from "../../../../stores/User/User.hooks";
import { useCreateSepaRefund } from "../../../../stores/Sepa/sepa.mutations";
import BankAccountSelector, { BankSelection, emptyNewBank } from "./BankAccountSelector";
import { bankSelectionToDto, validateBankSelection } from "./bankSelection";
import { billTAccountId } from "./sepaBill";

const REFUND_TEXT = "Rückerstattung des zu viel gezahlten Betrags an den Patienten: ";

const fmtEur = (value: number): string =>
  Number(value).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

interface Props {
  bill: PatientBillInfoDto;
  patientId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Refund of an overpaid bill via SEPA credit transfer (pick/enter the payee bank
 * account). Refunds are only allowed once the Rx is completed ('abgeschlossen'); a
 * chief may override for an earlier refund with a mandatory, audited reason.
 */
const SepaRefundDialog: React.FC<Props> = ({ bill, patientId, open, onOpenChange }) => {
  const locationId = useLocationId();
  const roles = useUserRole();
  const isChief = !!roles?.some((role) => role.id === "chief");
  const refund = useCreateSepaRefund();

  const [bank, setBank] = useState<BankSelection>(emptyNewBank);
  const [amount, setAmount] = useState<string>(String(Math.abs(bill.dueAmount ?? 0)));
  const [overrideReason, setOverrideReason] = useState<string>("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Rx not yet 'abgeschlossen' -> refund needs a chief override with a reason.
  const needsOverride = !bill.isRxCompleted;
  const currentPrice =
    bill.isEstimated && bill.isTimeSlotZero ? bill.estimatedPrice : bill.realPrice;

  // One idempotency key per "open" of the dialog = one intent. Reused across retries
  // (e.g. pressing Einreichen again after a failure) so the backend never creates a
  // duplicate refund; a fresh open starts a fresh intent.
  const idempotencyKeyRef = useRef<string>(randomUUID());
  useEffect(() => {
    if (open) idempotencyKeyRef.current = randomUUID();
  }, [open]);

  const reset = () => {
    setBank(emptyNewBank);
    setAmount(String(Math.abs(bill.dueAmount ?? 0)));
    setOverrideReason("");
    setErrors({});
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const handleSubmit = () => {
    // A refund on a not-yet-completed Rx is chief-only and needs a reason.
    if (needsOverride && !isChief) return;

    const nextErrors = validateBankSelection(bank);
    const numericAmount = Number(amount);
    if (!numericAmount || numericAmount <= 0) {
      nextErrors.amount = "Betrag muss positiv sein";
    }
    if (needsOverride && !overrideReason.trim()) {
      nextErrors.overrideReason = "Ein Grund für die vorzeitige Rückerstattung ist erforderlich.";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    refund.mutate(
      {
        patientId,
        accountId: bill.accountId,
        amount: numericAmount,
        text: `${REFUND_TEXT}${patientId}`,
        rxId: bill.rxId,
        tAccountId: billTAccountId(bill),
        businessId: bill.businessId,
        locationId,
        idempotencyKey: idempotencyKeyRef.current,
        ...bankSelectionToDto(bank),
        ...(needsOverride ? { override: true, overrideReason: overrideReason.trim() } : {}),
      },
      { onSuccess: close },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl">Rückerstattung</DialogTitle>
          <DialogDescription className="text-[15px]">
            Zu viel gezahlten Betrag für Rezept {bill.rxNumber} zurückerstatten.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Summary: already paid, current price, refund amount. */}
          <div className="grid grid-cols-3 gap-3 rounded-lg border border-solid border-slate-200 bg-slate-50/60 p-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Bereits gezahlt
              </p>
              <p className="mt-0.5 text-lg font-semibold text-slate-800">
                {fmtEur(bill.amountPaid)}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Aktueller Preis
              </p>
              <p className="mt-0.5 text-lg font-semibold text-slate-800">{fmtEur(currentPrice)}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Rückerstattung
              </p>
              <p className="mt-0.5 text-lg font-semibold text-rose-600">{fmtEur(Number(amount))}</p>
            </div>
          </div>

          {needsOverride && (
            <div className="space-y-2 rounded-lg border border-solid border-amber-300 bg-amber-50 p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                <p className="text-sm font-medium text-amber-800">
                  Das Rezept ist noch in Behandlung. Eine Rückerstattung ist während der laufenden
                  Behandlung normalerweise nicht möglich.
                </p>
              </div>
              {isChief ? (
                <div className="space-y-1">
                  <Label htmlFor="refund-override-reason" className="text-sm text-amber-800">
                    Grund für die vorzeitige Rückerstattung
                  </Label>
                  <Textarea
                    id="refund-override-reason"
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    rows={3}
                    placeholder="Grund angeben (wird protokolliert)"
                    disabled={refund.isPending}
                  />
                  {errors.overrideReason && (
                    <p className="text-sm text-rose-600">{errors.overrideReason}</p>
                  )}
                </div>
              ) : (
                <p className="text-sm font-semibold text-amber-800">
                  Nur Benutzer mit der Rolle „Chief" können eine vorzeitige Rückerstattung
                  freigeben.
                </p>
              )}
            </div>
          )}

          <BankAccountSelector
            patientId={patientId}
            value={bank}
            onChange={setBank}
            errors={errors}
            disabled={refund.isPending}
          />
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            className="text-base"
            onClick={() => onOpenChange(false)}
            disabled={refund.isPending}
          >
            Abbrechen
          </Button>
          <Button
            className="bg-blue-darkest text-base text-white hover:bg-blue-darkest/90"
            onClick={handleSubmit}
            disabled={refund.isPending || (needsOverride && !isChief)}
          >
            {refund.isPending ? "Wird eingereicht…" : "Einreichen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SepaRefundDialog;
